export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

/**
 * Firebase web app config for browser notifications (Web Push through Firebase Cloud Messaging).
 * Copy it from the Firebase console (Project settings → Your apps → Web app); the VAPID key is the
 * Web Push certificate's public key (Cloud Messaging tab). Without all five values browser
 * notifications are simply not offered.
 */
export const FIREBASE_WEB_CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
}
export const FIREBASE_VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY ?? ''
export const WEB_PUSH_CONFIGURED =
  Object.values(FIREBASE_WEB_CONFIG).every(Boolean) && !!FIREBASE_VAPID_KEY

export const QUERY_CLIENT_CONFIG = {
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
}

export const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

export const PERMISSION_GROUPS = [
  'WORKSPACE',
  'RECORD_GROUP',
  'FORM',
  'FORM_TEMPLATE',
  'RECORD',
  'AUTOMATION',
  'ACTIVITY',
  'USER',
  'ROLE',
  'SUBSCRIPTION',
  'PAYMENT',
  'NOTIFICATION',
  'DASHBOARD',
  'CALLING',
  'DEVELOPER',
] as const

export type PermissionGroup = (typeof PERMISSION_GROUPS)[number]

/**
 * Maps each permission group to the exact resource prefixes used in permission keys.
 *
 * This is needed because a naïve startsWith match is ambiguous:
 *   "form" would incorrectly match "form_template:*"
 *   "record" would incorrectly match "record_group:*"
 * Use getPermissionKeysForGroup() instead of rolling your own filter.
 */
export const PERMISSION_GROUP_RESOURCES: Record<string, string[]> = {
  WORKSPACE:     ['workspace'],
  RECORD_GROUP:  ['record_group'],
  FORM:          ['form'],
  FORM_TEMPLATE: ['form_template'],
  RECORD:        ['record'],
  AUTOMATION:    ['automation'],
  ACTIVITY:      ['record_activity'],
  USER:          ['user'],
  ROLE:          ['role'],
  SUBSCRIPTION:  ['subscription'],
  PAYMENT:       ['payment_order', 'payment_history'],
  NOTIFICATION:  ['notification'],
  DASHBOARD:     ['dashboard'],
  CALLING:       ['call'],
  DEVELOPER:     ['developer_dashboard'],
}

/**
 * Returns all permission key values (e.g. "record_group:create") that belong to
 * the given group.  Pass the PERMISSION_KEYS object from permission.store as the
 * second argument so this helper has no circular dependency on that module.
 */
export function getPermissionKeysForGroup(
  group: string,
  permissionKeys: Record<string, string>
): string[] {
  const resources = PERMISSION_GROUP_RESOURCES[group] ?? []
  return Object.values(permissionKeys).filter((key) =>
    resources.includes(key.split(':')[0])
  )
}
